import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import { useForkRef } from '@mui/material/utils';
import { useRef } from 'react';

import type { KeyboardEventHandler, ReactNode, Ref } from 'react';

export interface CrumbPopoverProps {
	anchorEl: HTMLElement | null;
	open: boolean;
	onClose: () => void;
	heading: string;
	search: string;
	onSearchChange: (value: string) => void;
	isPending: boolean;
	emptyLabel: string;
	isEmpty: boolean;
	children: ReactNode;
	searchInputLabel?: string;
	onSearchKeyDown?: KeyboardEventHandler<HTMLInputElement>;
	onPopoverKeyDown?: KeyboardEventHandler<HTMLDivElement>;
	menuListId?: string;
	menuListRef?: Ref<HTMLUListElement>;
	assistiveStatus?: ReactNode;
	disableRestoreFocus?: boolean;
}

/** Shared popover with a heading, search field, and item list. */
export function CrumbPopover({
	anchorEl,
	open,
	onClose,
	heading,
	search,
	onSearchChange,
	isPending,
	emptyLabel,
	isEmpty,
	children,
	searchInputLabel,
	onSearchKeyDown,
	onPopoverKeyDown,
	menuListId,
	menuListRef,
	assistiveStatus,
	disableRestoreFocus,
}: CrumbPopoverProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const internalMenuListRef = useRef<HTMLUListElement>(null);
	const mergedMenuListRef = useForkRef(internalMenuListRef, menuListRef);
	const handleSearchKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
		onSearchKeyDown?.(event);
		if (event.defaultPrevented || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) {
			return;
		}
		event.preventDefault();
		const enabledOptions = Array.from(
			internalMenuListRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? [],
		);
		enabledOptions[event.key === 'ArrowDown' ? 0 : enabledOptions.length - 1]?.focus();
	};

	return (
		<Popover
			anchorEl={anchorEl}
			open={open}
			onClose={onClose}
			disableRestoreFocus={disableRestoreFocus}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			slotProps={{
				paper: { className: 'mt-1 flex w-56 flex-col', onKeyDown: onPopoverKeyDown },
				transition: { onEntered: () => inputRef.current?.focus() },
			}}
		>
			<div className='py-1.5'>
				<p className='mb-1 p-1 px-2 text-xs font-semibold tracking-wide text-gray-a9 uppercase'>{heading}</p>
				<TextField
					inputRef={inputRef}
					placeholder='Search...'
					size='small'
					value={search}
					onChange={(e) => onSearchChange(e.target.value)}
					className='w-full bg-gray-3 py-1 [&_fieldset]:border-none'
					slotProps={{
						htmlInput: {
							'aria-label': searchInputLabel,
							'aria-controls': menuListId,
							onKeyDown: handleSearchKeyDown,
							className: 'py-1 text-sm',
						},
					}}
				/>
			</div>
			{assistiveStatus}
			<MenuList ref={mergedMenuListRef} id={menuListId} dense className='truncate max-h-80 overflow-y-auto'>
				{isPending ?
					<MenuItem disabled>
						<Skeleton width={120} />
					</MenuItem>
				: isEmpty ?
					<MenuItem disabled>{emptyLabel}</MenuItem>
				:	children}
			</MenuList>
		</Popover>
	);
}
